export interface VimeoEmbedRef {
  videoId: string;
  privacyHash?: string;
}

const VIMEO_REF_RE = /^(\d{10})(?:\?h=([a-fA-F0-9]+))?$/;
const VIMEO_URL_RE = /(?:vimeo\.com|player\.vimeo\.com\/video)\/(\d{10})(?:\?[^#]*h=([a-fA-F0-9]+))?/;

/** Parse a stored `welcome_video_vimeo_id` or a Vimeo URL into embed parts. */
export function parseVimeoEmbedRef(raw: string | null | undefined): VimeoEmbedRef | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return null;

  const match = trimmed.match(VIMEO_REF_RE) ?? trimmed.match(VIMEO_URL_RE);
  if (!match) return null;

  return {
    videoId: match[1],
    privacyHash: match[2] ?? undefined,
  };
}

/** Vimeo page URL for @vimeo/player when a privacy hash is required. */
export function vimeoPlayerUrl(ref: VimeoEmbedRef): string {
  const base = `https://vimeo.com/${ref.videoId}`;
  return ref.privacyHash ? `${base}?h=${ref.privacyHash}` : base;
}

/**
 * Rewrite an oEmbed thumbnail URL to a 1080 long-edge variant matching the video
 * aspect ratio. Defaults to 200×150 landscape and small CDN sizes return SMPTE
 * placeholders; the CDN path uses a picture hash (not the public video id) so we
 * have to rewrite an existing URL rather than construct one.
 */
export function upscaleVimeoThumbnailUrl(thumbnailUrl: string, videoWidth: number, videoHeight: number): string {
  if (!thumbnailUrl.includes('vimeocdn.com')) return thumbnailUrl;

  const stripped = stripPadParam(thumbnailUrl);
  if (videoWidth <= 0 || videoHeight <= 0) return stripped;

  const maxEdge = 1080;
  const scale = maxEdge / Math.max(videoWidth, videoHeight);
  const thumbW = Math.round(videoWidth * scale);
  const thumbH = Math.round(videoHeight * scale);

  const [path, query] = stripped.split('?');
  const upscaledPath = path.replace(/_\d+x\d+$/, `_${thumbW}x${thumbH}`);
  return query ? `${upscaledPath}?${query}` : upscaledPath;
}

/** Strip Vimeo's `?r=pad` letterbox parameter while preserving other query params. */
function stripPadParam(url: string): string {
  return url.replace(/([?&])r=pad(&|$)/, (_, prefix, suffix) => (suffix === '&' ? prefix : prefix === '?' ? '' : ''));
}
